import { Trans, useTranslation } from 'next-i18next';
import type { ReactNode } from 'react';
import { useRef } from 'react';

import { HIGH_FETCH_PRIORITY, V1A_NAMESPACE } from '../constants';
import { imageSrc, imageSrcSet } from './media';
import { useLivePingHud } from './use-live-ping-hud';

const SPARK_SETTLED = 'M0.0 55.2 L8.7 55.2 L17.4 55.4 L26.1 55.2 L34.8 55.0 L43.5 55.2 L52.2 55.2 L60.9 55.4 L69.6 55.2 L78.3 55.2 L87.0 55.0 L95.7 55.2 L104.3 55.4 L113.0 55.2 L121.7 55.2 L130.4 55.2 L139.1 55.0 L147.8 55.2 L156.5 55.2 L165.2 55.4 L173.9 55.2 L182.6 55.2 L191.3 55.0 L200.0 55.2';
const PRO_WIDTHS = [240, 480, 800, 1200, 1536] as const;
const GLOBE_WIDTHS = [480, 880] as const;
const GLOBE_SIZES = '(min-width: 64em) min(60vw, 740px), min(115vw, 820px)';
const QUOTE_SIZES = '(max-width: 47.99em) 205px, 228px';

const HIGHLIGHT = <span className='v1-hl !text-[#93FF18] [text-shadow:0_0_22px_rgb(147_255_24/.3)] forced-colors:!text-[LinkText]' />;

function LivePingHud() {
  const { t } = useTranslation(V1A_NAMESPACE);
  const hudRef = useRef<HTMLDivElement>(null);
  useLivePingHud(hudRef);

  return (
    <div ref={hudRef} className='v1-hud' data-reveal='' data-hud=''>
      <div className='v1-hud__head'>
        <span className='v1-hud__live'>
          <span className='v1-hud__pulse' aria-hidden='true' />
          {t('hero.hud.live')}
        </span>
        <span className='v1-hud__route' aria-hidden='true'>
          <Trans t={t} i18nKey='hero.hud.route' components={{ arrow: <span className='v1-hud__arrow' /> }} />
        </span>
      </div>
      <div className='v1-hud__body'>
        <div className='v1-hud__read'>
          <p className='v1-hud__label'>{t('hero.hud.label')}</p>
          <p className='v1-hud__value'>
            <span className='sr-only'>{t('hero.hud.value_sr')}</span>
            <span className='v1-hud__num tabular' data-ping-value='' aria-hidden='true'>
              4
            </span>
            <span className='v1-hud__unit' aria-hidden='true'>
              ms
            </span>
            <span className='v1-hud__pct tabular' data-ping-pct='' aria-hidden='true'>
              −98%
            </span>
          </p>
        </div>
        <div className='v1-hud__chart' role='img' aria-label={t('hero.hud.chart_label')}>
          <svg viewBox='0 0 200 64' width='200' height='64' preserveAspectRatio='none' aria-hidden='true' focusable='false'>
            <defs>
              <linearGradient id='v1-spark-fill' x1='0' y1='0' x2='0' y2='1'>
                <stop offset='0' stopColor='#93FF18' stopOpacity='.28' />
                <stop offset='.65' stopColor='#93FF18' stopOpacity='.05' />
                <stop offset='1' stopColor='#93FF18' stopOpacity='0' />
              </linearGradient>
            </defs>
            <g stroke='#FFFFFF' strokeOpacity='.07' strokeWidth='1'>
              <path d='M0 12h200M0 32h200M0 52h200' />
            </g>
            <path className='v1-hud__area' d={`${SPARK_SETTLED} L200 64 L0 64 Z`} fill='url(#v1-spark-fill)' />
            <path className='v1-hud__line' d={SPARK_SETTLED} fill='none' stroke='#93FF18' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
          </svg>
          <span className='v1-hud__dot [--dot-y:86.22%]' aria-hidden='true' />
        </div>
      </div>
      <div className='v1-hud__bars'>
        <div className='v1-hud__bar v1-hud__bar--bad'>
          <span className='v1-hud__bar-label'>{t('hero.hud.without')}</span>
          <span className='v1-hud__track'>
            <i className='[--w:93.07%]' data-ping-bad-bar='' />
          </span>
          <span className='v1-hud__bar-num tabular'>
            <span className='sr-only'>{t('hero.hud.bad_sr')}</span>
            <span aria-hidden='true'>
              <span data-ping-bad=''>208</span> <small>ms</small>
            </span>
          </span>
        </div>
        <div className='v1-hud__bar v1-hud__bar--good'>
          <span className='v1-hud__bar-label'>{t('hero.hud.with')}</span>
          <span className='v1-hud__track'>
            <i className='[--w:4.73%]' data-ping-good-bar='' />
          </span>
          <span className='v1-hud__bar-num tabular'>
            <span className='sr-only'>{t('hero.hud.good_sr')}</span>
            <span aria-hidden='true'>
              <span data-ping-bar=''>4</span> <small>ms</small>
            </span>
          </span>
        </div>
      </div>
      <p className='v1-hud__note'>{t('hero.hud.note')}</p>
    </div>
  );
}

export function Hero({ form }: { form: ReactNode }) {
  const { t } = useTranslation(V1A_NAMESPACE);

  return (
    <section className='v1-hero relative isolate overflow-x-clip pb-[clamp(3rem,1.5rem_+_5vw,5.5rem)] pt-[clamp(1.25rem,.9rem_+_1.2vw,1.875rem)]' aria-labelledby='v1-title'>
      <div className='v1-hero__bg' aria-hidden='true'>
        <span className='v1-stars' />
        <span className='v1-hero__bloom' />
        <span className='v1-hero__bloom v1-hero__bloom--right' />
        <span className='v1-hero__grid' />
        <span className='v1-hero__globe'>
          <picture>
            <source type='image/avif' srcSet={imageSrcSet('hero-globe', GLOBE_WIDTHS, 'avif')} sizes={GLOBE_SIZES} />
            <img className='v1-hero__globe-media' src={imageSrc('hero-globe-880.webp')} srcSet={imageSrcSet('hero-globe', GLOBE_WIDTHS, 'webp')} sizes={GLOBE_SIZES} width='880' height='880' alt='' decoding='async' {...HIGH_FETCH_PRIORITY} />
          </picture>
          <span className='v1-hero__orbit' />
        </span>
        <span className='v1-hero__horizon' />
      </div>

      <div className='v1-hero__inner mx-auto grid w-full max-w-[90rem] items-start gap-[clamp(2rem,1rem_+_3vw,3.5rem)] px-[var(--gutter)] *:min-w-0 lg:grid-cols-[minmax(0,1fr)_var(--form-max)] lg:gap-[clamp(2.5rem,1rem_+_3.5vw,4.5rem)]'>
        <div className='v1-hero__copy min-w-0'>
          <p className='v1-eyebrow v1-eyebrow--chip m-0 inline-flex items-center gap-2 rounded-full border border-[rgb(147_255_24/.28)] bg-[linear-gradient(180deg,rgb(147_255_24/.14),rgb(147_255_24/.05))] py-[.4375rem] pe-[.8125rem] ps-[.6875rem] text-[length:var(--t-micro)] font-semibold uppercase leading-[1.3] tracking-[.16em] text-[#D4FF27] shadow-[inset_0_1px_0_rgb(255_255_255/.08),0_8px_24px_rgb(0_0_0/.35)]' data-reveal=''>
            <span className='v1-eyebrow__dot size-1.5 flex-none rounded-full bg-[#93FF18] shadow-[0_0_10px_rgb(147_255_24/.8)]' aria-hidden='true' />
            {t('hero.eyebrow')}
          </p>

          <h1 className='v1-hero__title mx-0 mb-0 mt-3 max-w-[15ch] text-balance text-[length:clamp(2.125rem,1.1rem_+_2.8vw,3.25rem)] font-bold leading-[1.0] tracking-[-.035em] text-[#FFFFFF] min-[1920px]:text-[length:3.75rem]' id='v1-title' data-reveal=''>
            <Trans t={t} i18nKey='hero.title' components={{ hl: HIGHLIGHT }} />
          </h1>

          <p className='v1-hero__lead mx-0 mb-0 mt-4 max-w-[34rem] text-pretty text-[length:var(--t-lead)] leading-normal text-[#CBD5E1] min-[1440px]:max-w-[38rem] [&_strong]:font-semibold [&_strong]:text-[#FFFFFF]' data-reveal=''>
            <Trans t={t} i18nKey='hero.lead' components={{ strong: <strong /> }} />
          </p>

          <LivePingHud />

          <figure className='v1-quote relative mx-0 mb-0 mt-3.5 grid max-w-[34rem] grid-cols-[5rem_minmax(0,1fr)] items-center gap-3.5 py-3 pe-[1.125rem] ps-0 max-md:grid-cols-[4.5rem_minmax(0,1fr)]' data-reveal=''>
            <span className='v1-quote__frame relative ms-3 aspect-square w-full flex-none self-center overflow-hidden max-md:ms-2.5'>
              <picture>
                <source type='image/avif' srcSet={imageSrcSet('pro-sacy', PRO_WIDTHS, 'avif')} sizes={QUOTE_SIZES} />
                <img className='v1-quote__photo absolute inset-0 size-full origin-[50%_20%] scale-[1.9] object-cover object-[6%_50%]' src={imageSrc('pro-sacy-240.webp')} srcSet={imageSrcSet('pro-sacy', PRO_WIDTHS, 'webp')} sizes={QUOTE_SIZES} width='1536' height='1024' alt={t('hero.quote.photo_alt')} decoding='async' />
              </picture>
            </span>
            <div className='v1-quote__body min-w-0 py-0'>
              <blockquote className='v1-quote__text m-0 hyphens-auto text-pretty text-[length:var(--t-body)] font-medium leading-[1.45] text-[#F4F6F7]'>{t('hero.quote.text')}</blockquote>
              <figcaption className='v1-quote__cap mt-[.4375rem] text-[length:var(--t-micro)] text-[#8C95A8] [&_strong]:font-semibold [&_strong]:text-[#93FF18]'>
                <strong>Sacy</strong> <span>{t('hero.quote.role')}</span>
              </figcaption>
            </div>
          </figure>

          <p className='v1-cert mx-0 mb-0 mt-3.5 flex flex-wrap items-center gap-x-[1.125rem] gap-y-2.5' data-reveal=''>
            <span className='v1-cert__label text-[length:var(--t-micro)] font-semibold uppercase tracking-[.14em] text-[#8C95A8]'>{t('hero.cert.label')}</span>
            <img className='v1-cert__ms h-6 w-auto opacity-75' src={imageSrc('cert-microsoft-96.webp')} width='96' height='99' alt='Microsoft' loading='lazy' decoding='async' />
            <img className='v1-cert__dc h-[1.375rem] w-auto opacity-75' src={imageSrc('cert-digicert-128.webp')} width='128' height='52' alt='DigiCert' loading='lazy' decoding='async' />
            <span className='v1-cert__os ps-[1.125rem] text-[length:var(--t-micro)] text-[#8C95A8] max-[400px]:ps-0 lg:max-[1120px]:ps-0'>{t('hero.cert.os')}</span>
          </p>
        </div>

        <div className='v1-hero__form relative grid min-w-0 justify-items-center gap-3 max-lg:justify-items-stretch' id='cadastro' data-cadastro=''>
          <p className='v1-formtag m-0 inline-flex items-center gap-2 py-1.5 pe-3 ps-2.5 text-[length:var(--t-micro)] font-semibold uppercase tracking-[.14em] text-[#CBD5E1] max-lg:justify-self-start'>
            <span className='v1-formtag__dot' aria-hidden='true' />
            {t('hero.form_tag')}
          </p>
          {form}
        </div>
      </div>
    </section>
  );
}
